import { useCallback, useEffect, useRef, useState } from 'react';
import { Call, Device } from '@twilio/voice-sdk';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeVoiceTokenErrorSelector,
  CallScribeVoiceTokenLoadingSelector,
  CallScribeVoiceTokenSelector,
} from 'store/callScribe/selectors';
import { ThunkGetCallScribeVoiceToken } from 'store/callScribe/thunks';
import { patchCallScribeLifecycle } from 'services/callScribe/lifecycle';
import { twilioErrorCode } from 'components/CallScribe/twilioErrorCode';
import { CallScribeCallStatus } from 'services/callScribe/types';

const BASE_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

type IsolatingAudioConstraints = MediaTrackConstraints & { voiceIsolation?: boolean };

const ISOLATED_AUDIO_CONSTRAINTS: IsolatingAudioConstraints = {
  ...BASE_AUDIO_CONSTRAINTS,
  voiceIsolation: true,
};

const applyAudioConstraints = async (device: Device): Promise<void> => {
  const audio = device.audio;
  if (!audio) {
    return;
  }
  try {
    await audio.setAudioConstraints(ISOLATED_AUDIO_CONSTRAINTS);
  } catch {
    try {
      await audio.setAudioConstraints(BASE_AUDIO_CONSTRAINTS);
    } catch {
      await audio.unsetAudioConstraints().catch(() => undefined);
    }
  }
};

const reportAudioSettings = (call: Call): void => {
  const track = call.getLocalStream()?.getAudioTracks()[0];
  if (!track) {
    return;
  }
  const s = track.getSettings() as MediaTrackSettings & { voiceIsolation?: boolean };
  console.info('[CallScribe] capture settings', {
    echoCancellation: s.echoCancellation,
    noiseSuppression: s.noiseSuppression,
    autoGainControl: s.autoGainControl,
    voiceIsolation: s.voiceIsolation,
    label: track.label,
  });
};

export type TwilioDeviceState =
  | 'idle' // No token yet / device not registered
  | 'registering' // Token in flight or device registering
  | 'ready' // Device registered and able to place calls
  | 'connecting' // Outbound call in setup
  | 'in-call' // Active call
  | 'error'; // Token mint failed or device errored

export interface IUseTwilioDeviceResult {
  state: TwilioDeviceState;
  errorMessage: string | undefined;
  /** Twilio error code behind `errorMessage`, when the failure came from the SDK. */
  errorCode: number | undefined;
  /** Clears an error state when the Device is still registered, without a reload. */
  recover: () => void;
  /** Places an outbound call via the registered Device using the configured TwiML App. */
  placeCall: (params: { to: string; callId?: number }) => Promise<void>;
  hangup: () => void;
  mute: (shouldMute: boolean) => void;
  isMuted: boolean;
  /** Active Twilio Call object — exposed for advanced UI (DTMF, transfer, etc.). */
  activeCall: Call | undefined;
  /** Available browser input (microphone) devices. */
  inputDevices: MediaDeviceInfo[];
  /** Currently selected input device id (Twilio's choice if not explicitly set). */
  selectedInputDeviceId: string | undefined;
  /** Switch microphone live; works mid-call. */
  setInputDevice: (deviceId: string) => Promise<void>;
  /** Live RMS-style volume of the local mic (0–1). 0 = silence, > 0 = audio is being captured. */
  inputVolume: number;
  /** Live RMS-style volume of the inbound (remote) audio stream (0–1). */
  outputVolume: number;
  /** Re-prompts the OS for mic permission and refreshes the device list. */
  refreshAudioDevices: () => Promise<void>;
}

/**
 * Browser-side Twilio Voice Device manager.
 *
 * - Mints a token via `/api/call-scribe/calls/voice-token` on mount and registers a Device.
 * - Refreshes the token ~30s before expiry to keep the Device alive across long sessions.
 * - Tears the Device down on unmount.
 *
 * Returns a small action surface (`placeCall`, `hangup`, `mute`) plus the active call for the
 * Softphone UI to render mute/hangup controls.
 */
export const useTwilioDevice = (): IUseTwilioDeviceResult => {
  const dispatch = useAppDispatch();
  const token = useAppSelector(CallScribeVoiceTokenSelector);
  const tokenLoading = useAppSelector(CallScribeVoiceTokenLoadingSelector);
  const tokenError = useAppSelector(CallScribeVoiceTokenErrorSelector);

  const deviceRef = useRef<Device | undefined>(undefined);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // CallScribeCall.Id for the call currently in flight, captured at placeCall time.
  // Used by the Twilio Call event handlers to PATCH lifecycle without prop-drilling.
  const activeCallIdRef = useRef<number | undefined>(undefined);
  // Wall-clock millis when the call was dialled, used to compute DurationSeconds at end.
  const startedAtRef = useRef<number | undefined>(undefined);
  // Wall-clock millis when the remote leg picked up. Used to compute DurationSeconds and
  // to distinguish a true "Completed" disconnect from a "Cancelled" hangup-before-answer.
  const mediaEstablishedAtRef = useRef<number | undefined>(undefined);

  const legRef = useRef<Call | 'placing' | null>(null);

  const [state, setState] = useState<TwilioDeviceState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [errorCode, setErrorCode] = useState<number | undefined>(undefined);
  const [activeCall, setActiveCall] = useState<Call | undefined>(undefined);
  const [isMuted, setIsMuted] = useState(false);
  const [inputDevices, setInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedInputDeviceId, setSelectedInputDeviceId] = useState<string | undefined>(undefined);
  const [inputVolume, setInputVolume] = useState(0);
  const [outputVolume, setOutputVolume] = useState(0);

  /**
   * Best-effort lifecycle PATCH. Wraps {@link patchCallScribeLifecycle} with a
   * no-op when we don't have a call id (e.g. legacy callers that hand-roll a dial
   * without first POSTing `/call-scribe/calls`) and a silent catch so a network
   * blip during the disconnect handler never tears the React tree down.
   */
  const emitLifecyclePatch = useCallback(
    (body: {
      status?: CallScribeCallStatus;
      startedAtUtc?: string;
      answeredAtUtc?: string;
      endedAtUtc?: string;
      durationSeconds?: number;
    }) => {
      const callId = activeCallIdRef.current;
      if (typeof callId !== 'number') {
        return;
      }
      void patchCallScribeLifecycle(callId, body).catch(() => {
        // BE will fall back to the Twilio status callback for any PATCH we miss.
      });
    },
    [],
  );

  const refreshAudioDevices = useCallback(async () => {
    // Enumerate AFTER permission is granted — device labels are empty strings until then,
    // which makes the picker useless. We trigger a transient getUserMedia to force labels.
    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
      tmp.getTracks().forEach(t => { t.stop(); });
    } catch {
      // If permission was denied we'll still enumerate — labels will just be empty.
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter(d => d.kind === 'audioinput');
    setInputDevices(inputs);
    // Mirror Twilio's currently selected mic so the picker reflects reality.
    const device = deviceRef.current;
    const audio = device?.audio;
    if (audio?.inputDevice?.deviceId) {
      setSelectedInputDeviceId(audio.inputDevice.deviceId);
    } else if (inputs[0]?.deviceId) {
      setSelectedInputDeviceId(inputs[0].deviceId);
    }
  }, []);

  const setInputDevice = useCallback(async (deviceId: string) => {
    const device = deviceRef.current;
    if (!device?.audio) return;
    try {
      await device.audio.setInputDevice(deviceId);
      setSelectedInputDeviceId(deviceId);
    } catch (err) {
      setErrorMessage(err instanceof Error ? `Could not switch mic: ${err.message}` : 'Could not switch microphone');
    }
  }, []);

  // Initial token fetch.
  useEffect(() => {
    if (!token && !tokenLoading && !tokenError) {
      setState('registering');
      void dispatch(ThunkGetCallScribeVoiceToken());
    }
  }, [token, tokenLoading, tokenError, dispatch]);

  // Surface token errors to the UI.
  useEffect(() => {
    if (tokenError) {
      setState('error');
      setErrorMessage(tokenError);
    }
  }, [tokenError]);

  // Register the Device whenever a token is available.
  useEffect(() => {
    if (!token?.accessToken) {
      return undefined;
    }

    let cancelled = false;
    const device = new Device(token.accessToken, {
      // Default codec preference — Opus for quality, PCMU as fallback for compatibility.
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      logLevel: 'warn',
    });

    deviceRef.current = device;
    void applyAudioConstraints(device);

    device.on('registered', () => {
      if (!cancelled) {
        setState('ready');
        setErrorMessage(undefined);
        setErrorCode(undefined);
      }
    });

    device.on('error', (err: { message?: string; code?: number }) => {
      if (!cancelled) {
        setState('error');
        setErrorMessage(err?.message ?? `Twilio Device error (code ${err?.code ?? 'unknown'})`);
        setErrorCode(typeof err?.code === 'number' ? err.code : undefined);
      }
    });

    device.on('tokenWillExpire', () => {
      // Force a refresh ~30s before expiry — see refresh timer below as a safety net.
      void dispatch(ThunkGetCallScribeVoiceToken());
    });

    device.register().catch((err: unknown) => {
      if (!cancelled) {
        setState('error');
        const msg =
          err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
            ? (err as { message: string }).message
            : 'Failed to register Twilio Device';
        setErrorMessage(msg);
        setErrorCode(twilioErrorCode(err));
      }
    });

    // Schedule a refresh ahead of expiry.
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    const refreshMs = Math.max((token.ttlSeconds - 30) * 1000, 30_000);
    refreshTimerRef.current = setTimeout(() => {
      void dispatch(ThunkGetCallScribeVoiceToken());
    }, refreshMs);

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = undefined;
      }
      try {
        device.destroy();
      } catch {
        // Ignore destroy errors — best-effort teardown.
      }
      deviceRef.current = undefined;
    };
  }, [token, dispatch]);

  // Update the device with a fresh token when it arrives without re-creating the instance,
  // when the device already exists. The effect above re-creates on first token only because
  // deviceRef is null then.
  useEffect(() => {
    const device = deviceRef.current;
    if (device && token?.accessToken && device.state === Device.State.Registered) {
      try {
        device.updateToken(token.accessToken);
      } catch {
        // If updateToken fails, the next 'tokenWillExpire' / 'error' will retry.
      }
    }
  }, [token]);

  const placeCall = useCallback(
    async ({ to, callId }: { to: string; callId?: number }) => {
      if (legRef.current !== null) return;
      setErrorMessage(undefined);
      setErrorCode(undefined);
      const device = deviceRef.current;
      if (!device || device.state !== Device.State.Registered) {
        setErrorMessage('Softphone is not ready yet.');
        setState('error');
        return;
      }
      legRef.current = 'placing';
      const endLeg = (call?: Call): void => {
        if (legRef.current === (call ?? 'placing')) legRef.current = null;
      };
      setState('connecting');
      try {
        // Pre-warm mic with an explicit getUserMedia call so we surface a clear permission /
        // device error to the user BEFORE handing off to Twilio's SDK. The SDK's own
        // 31402 AcquisitionFailedError is opaque ("bad constraints, browser, OS or hardware");
        // running the prompt ourselves means a denied/missing mic shows the native message.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          // Release immediately — Twilio's SDK will re-acquire the mic on connect.
          stream.getTracks().forEach(t => { t.stop(); });
        } catch (mediaErr) {
          const m = mediaErr instanceof Error ? mediaErr.message : 'Microphone access failed';
          setErrorMessage(`Microphone unavailable: ${m}. Allow microphone access for this site and try again.`);
          setState('error');
          endLeg();
          return;
        }

        // Params land in the TwiML App webhook as POST form fields, which is how the BE
        // correlates the browser leg with the persisted CallScribeCall row.
        const params: Record<string, string> = { To: to };
        if (typeof callId === 'number') params.CallScribeCallId = String(callId);

        const call = await device.connect({ params });
        if (legRef.current === 'placing') legRef.current = call;
        setActiveCall(call);
        setIsMuted(false);

        // Capture lifecycle timestamps for the FE-trusted PATCH path. The BE merges these
        // forward-only — a Twilio status callback that arrives later cannot regress them.
        activeCallIdRef.current = typeof callId === 'number' ? callId : undefined;
        startedAtRef.current = Date.now();
        mediaEstablishedAtRef.current = undefined;
        // Optimistic Ringing PATCH so the dashboard / SignalR consumers see the call has
        // left Initiated even before the carrier returns the first SIP status.
        emitLifecyclePatch({
          status: CallScribeCallStatus.Ringing,
          startedAtUtc: new Date(startedAtRef.current).toISOString(),
        });

        // Live volume monitoring — lets the UI show a mic-level meter so the user can verify
        // their mic is actually capturing audio. Twilio fires this ~10x/sec while connected.
        // `inputVol` and `outputVol` are 0–1 RMS-ish values.
        call.on('volume', (inputVol: number, outputVol: number) => {
          setInputVolume(inputVol);
          setOutputVolume(outputVol);
        });

        // Refresh the device list now that we definitely have mic permission — only after
        // permission do device labels become readable.
        void refreshAudioDevices();

        reportAudioSettings(call);

        call.on('accept', () => {
          setState('in-call');
          // Media with Twilio is established, which is NOT the callee answering: the
          // TwiML does not use answerOnBridge, so this fires while the customer is
          // still ringing. Claiming Answered here wrote a browser-accept timestamp
          // into answeredAtUtc that the forward-only merge then preferred over
          // Twilio's real answer time, so the row is left to the status callback.
          mediaEstablishedAtRef.current = Date.now();
        });
        call.on('disconnect', () => {
          endLeg(call);
          setState('ready');
          setActiveCall(undefined);
          setIsMuted(false);
          setInputVolume(0);
          setOutputVolume(0);
          // Answered → disconnect = Completed; never-answered → disconnect = Cancelled
          // (the tech hung up before the remote leg picked up).
          const endedAt = Date.now();
          const mediaWasUp = typeof mediaEstablishedAtRef.current === 'number';
          // durationSeconds is deliberately not sent: measured from browser accept it
          // would include ringing and consent time. Twilio's status callback carries
          // the answer-based duration.
          emitLifecyclePatch({
            status: mediaWasUp ? CallScribeCallStatus.Completed : CallScribeCallStatus.Cancelled,
            endedAtUtc: new Date(endedAt).toISOString(),
          });
          // Clear refs so a subsequent placeCall starts from a clean state.
          activeCallIdRef.current = undefined;
          startedAtRef.current = undefined;
          mediaEstablishedAtRef.current = undefined;
        });
        call.on('cancel', () => {
          endLeg(call);
          setState('ready');
          setActiveCall(undefined);
          setInputVolume(0);
          setOutputVolume(0);
          emitLifecyclePatch({
            status: CallScribeCallStatus.Cancelled,
            endedAtUtc: new Date().toISOString(),
            durationSeconds: 0,
          });
          activeCallIdRef.current = undefined;
          startedAtRef.current = undefined;
          mediaEstablishedAtRef.current = undefined;
        });
        call.on('error', (err: { message?: string; code?: number }) => {
          endLeg(call);
          setErrorMessage(err?.message ?? 'Call error');
          setErrorCode(typeof err?.code === 'number' ? err.code : undefined);
          setState('ready');
          setActiveCall(undefined);
          emitLifecyclePatch({
            status: CallScribeCallStatus.Failed,
            endedAtUtc: new Date().toISOString(),
          });
          activeCallIdRef.current = undefined;
          startedAtRef.current = undefined;
          mediaEstablishedAtRef.current = undefined;
        });
      } catch (err) {
        endLeg();
        setState('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to place call');
        setErrorCode(twilioErrorCode(err));
      }
    },
    [refreshAudioDevices, emitLifecyclePatch],
  );

  const recover = useCallback(() => {
    legRef.current = null;
    const device = deviceRef.current;
    if (device) {
      try {
        device.disconnectAll();
      } catch { }
    }
    setActiveCall(undefined);
    setIsMuted(false);
    setInputVolume(0);
    setOutputVolume(0);
    setErrorMessage(undefined);
    setErrorCode(undefined);
    setState(device && device.state === Device.State.Registered ? 'ready' : 'registering');
  }, []);

  const hangup = useCallback(() => {
    if (activeCall) {
      try {
        activeCall.disconnect();
      } catch {
        // Ignore disconnect errors.
      }
    }
  }, [activeCall]);

  const mute = useCallback(
    (shouldMute: boolean) => {
      if (activeCall) {
        try {
          activeCall.mute(shouldMute);
          setIsMuted(shouldMute);
        } catch {
          // Ignore mute errors.
        }
      }
    },
    [activeCall],
  );

  return {
    state,
    errorMessage,
    errorCode,
    recover,
    placeCall,
    hangup,
    mute,
    isMuted,
    activeCall,
    inputDevices,
    selectedInputDeviceId,
    setInputDevice,
    inputVolume,
    outputVolume,
    refreshAudioDevices,
  };
};
